import type { ControlsState } from './Controls';
import { useDriverAttribution } from './useHaloV2';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm';
const usd = (v: number) => `$${Math.round(v).toLocaleString()}`;

export function DriversCard({ state }: { state: ControlsState }) {
  const { data, isLoading } = useDriverAttribution(state);
  return (
    <div className={cardCls}>
      <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Drivers (estimated)</h3>
      {isLoading || !data ? (
        <p className="text-gray-400 text-xs">Loading…</p>
      ) : data.drivers.length === 0 ? (
        <p className="text-gray-400 text-xs">
          No attribution rows in range — run a backfill (attribution computes with baselines).
        </p>
      ) : (
        <>
          {data.drivers.map((d) => (
            <div key={d.driver} className="py-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-300">
                  {d.driver.replace(/_/g, ' ')}
                </span>
                <span className="font-semibold tabular-nums">{usd(d.attributedHalo)}</span>
              </div>
              <div className="mt-0.5 h-1.5 rounded bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-1.5 rounded bg-green-600"
                  style={{ width: `${Math.min(100, d.share * 100).toFixed(1)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400">
                fit {d.avgFitScore.toFixed(2)} · lag {d.bestLag}d · conf{' '}
                {d.dataConfidence.toFixed(2)}
              </p>
            </div>
          ))}
          {data.totals.unattributed !== 0 ? (
            <p
              className={`mt-1 text-xs ${data.totals.unattributed < 0 ? 'text-amber-600 dark:text-amber-500' : 'text-gray-500 dark:text-gray-400'}`}
            >
              Unattributed: {usd(data.totals.unattributed)}
              {data.totals.unattributed < 0
                ? ' (attribution exceeds baseline halo in range — partial backfill?)'
                : ''}
            </p>
          ) : null}
          <p className="mt-2 text-[10px] text-gray-400">
            Estimated allocation of daily positive halo (gross) — see the range summary for the
            noise-corrected attributable tier.
          </p>
        </>
      )}
    </div>
  );
}
